
import { Rule } from "@/lib/types";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Edit, Octagon, OctagonX, Trash, X } from "lucide-react";
import { toggleRuleStatus } from "@/services/ruleService";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface RuleItemProps {
  rule: Rule;
  onEdit: (rule: Rule) => void;
  onDelete: (rule: Rule) => void;
  disableDrag?: boolean;
}

export function RuleItem({ rule, onEdit, onDelete, disableDrag = false }: RuleItemProps) {
  const [isEnabled, setIsEnabled] = useState(rule.enabled);
  const [isUpdating, setIsUpdating] = useState(false);
  
  const handleToggle = async (checked: boolean) => {
    setIsUpdating(true);
    const success = await toggleRuleStatus(rule.id, checked);
    if (success) {
      setIsEnabled(checked);
    }
    setIsUpdating(false);
  };

  // Get counts for rendering
  const conditionCount = rule.conditions?.length || 0;
  const actionCount = rule.actions?.length || 0;
  
  // Check if this rule has a stop action
  const hasStopAction = rule.actions?.some(action => action.type === 'stop' && action.value === true);
  
  return (
    <>
      <div className="font-medium truncate">{rule.name}</div>
      <div className="text-sm">
        {conditionCount === 0 ? (
          <Badge variant="outline" className="bg-blue-50 text-blue-700 hover:bg-blue-100">All emails</Badge>
        ) : (
          <div>
            {conditionCount} {conditionCount === 1 ? 'condition' : 'conditions'}
          </div>
        )}
      </div>
      <div className="text-sm">
        <div className="flex items-center gap-2">
          <span>{actionCount} {actionCount === 1 ? 'action' : 'actions'}</span>
          {hasStopAction && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge variant="secondary" className="bg-amber-100 text-amber-700 hover:bg-amber-200">
                    Stop
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="text-xs">This rule stops processing further rules</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      </div>
      <div>
        <Switch
          checked={isEnabled}
          onCheckedChange={handleToggle}
          disabled={isUpdating}
          className="data-[state=checked]:bg-green-500"
        />
      </div>
      <div className="flex justify-end gap-2">
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => onEdit(rule)}
        >
          <Edit className="h-4 w-4" />
        </Button>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => onDelete(rule)}
          className="text-destructive hover:text-destructive hover:bg-destructive/10"
        >
          <OctagonX className="h-4 w-4" />
        </Button>
      </div>
    </>
  );
}
